import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { FiChevronDown } from "react-icons/fi";
import "./GetInTouch.css";
import { useTranslation } from "react-i18next";
import { getCourses } from "../../../../api/courses";
import { createContactUsRequest } from "../../../../api/contactUs";
import { scrollToElementById } from "../../../../utils/scrollToId";

const EXPERIENCE_OPTIONS = [
  { value: "0-1 years", key: "y01" },
  { value: "1-3 years", key: "y13" },
  { value: "3-5 years", key: "y35" },
  { value: "5-10 years", key: "y510" },
  { value: "10+ years", key: "y10plus" },
];

const GetInTouch = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [courses, setCourses] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [form, setForm] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    location: "",
    courseId: "",
    yearsOfExperience: "",
  });

  useEffect(() => {
    let isMounted = true;
    const loadCourses = async () => {
      try {
        const data = await getCourses({ language: String(i18n.language || "en").toLowerCase() });
        if (!isMounted) return;
        const list = Array.isArray(data) ? data : [];
        setCourses(list);
        setForm((prev) => ({
          ...prev,
          courseId: prev.courseId || String(list[0]?.id || ""),
        }));
      } catch {
        if (!isMounted) return;
        setCourses([]);
      }
    };
    loadCourses();
    return () => {
      isMounted = false;
    };
  }, [i18n.language]);

  useEffect(() => {
    if (location.hash !== "#contact-form") return;
    const frame = window.requestAnimationFrame(() => {
      scrollToElementById("contact-form");
    });
    return () => window.cancelAnimationFrame(frame);
  }, [location.pathname, location.hash]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!String(form.name || "").trim()) {
      setError(t("academyForm.errors.nameRequired"));
      return;
    }
    if (!String(form.email || "").trim()) {
      setError(t("academyForm.errors.emailRequired"));
      return;
    }
    if (!String(form.location || "").trim()) {
      setError(t("academyForm.errors.locationRequired"));
      return;
    }
    if (!String(form.courseId || "").trim()) {
      setError(t("academyForm.errors.courseRequired"));
      return;
    }
    if (!String(form.yearsOfExperience || "").trim()) {
      setError(t("academyForm.errors.experienceRequired"));
      return;
    }

    setSubmitting(true);
    try {
      await createContactUsRequest({
        name: String(form.name || "").trim(),
        email: String(form.email || "").trim(),
        phoneNumber: String(form.phoneNumber || "").trim() || null,
        location: String(form.location || "").trim(),
        courseId: Number(form.courseId),
        yearsOfExperience: String(form.yearsOfExperience || "").trim(),
      });
      setSuccess(t("academyForm.success"));
      setForm({
        name: "",
        email: "",
        phoneNumber: "",
        location: "",
        courseId: String(courses[0]?.id || ""),
        yearsOfExperience: "",
      });
    } catch (submitError) {
      setError(submitError?.response?.data?.message || t("academyForm.errors.submitFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact-form" className="academyContact" aria-label="Get in touch">
      <div className="academyContact-inner">
        <div className="academyContact-left">
          <span className="academyContact-tag">{t("sections.academy.getInTouch.tag")}</span>

          <h2 className="academyContact-title">
            {t("sections.academy.getInTouch.title")}{" "}
            <span>{t("sections.academy.getInTouch.titleAccent")}</span>
          </h2>

          <p className="academyContact-text">{t("sections.academy.getInTouch.text")}</p>
        </div>

        <div className="academyContact-right">
          <h3 className="academyContact-formTitle">{t("sections.academy.getInTouch.formTitle")}</h3>

          <form className="academyContact-form" onSubmit={handleSubmit}>
            <div className="academyContact-row academyContact-rowHalf">
              <input
                type="text"
                placeholder={t("academyForm.namePlaceholder")}
                aria-label={t("academyForm.nameAriaLabel")}
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              />
              <input
                type="email"
                placeholder={t("academyForm.emailPlaceholder")}
                aria-label={t("academyForm.emailAriaLabel")}
                value={form.email}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
              />
            </div>

            <div className="academyContact-row">
              <input
                type="text"
                placeholder={t("academyForm.phonePlaceholder")}
                aria-label={t("academyForm.phoneAriaLabel")}
                value={form.phoneNumber}
                onChange={(e) => setForm((p) => ({ ...p, phoneNumber: e.target.value }))}
              />
            </div>

            <div className="academyContact-row">
              <input
                type="text"
                placeholder={t("academyForm.locationPlaceholder")}
                aria-label={t("academyForm.locationAriaLabel")}
                value={form.location}
                onChange={(e) => setForm((p) => ({ ...p, location: e.target.value }))}
              />
            </div>

            <div className="academyContact-row academyContact-selectLike">
              <select
                value={form.courseId}
                onChange={(e) => setForm((p) => ({ ...p, courseId: e.target.value }))}
                aria-label={t("academyForm.courseAriaLabel")}
              >
                {courses.length ? (
                  courses.map((course) => (
                    <option key={course.id} value={String(course.id)}>
                      {course.title}
                    </option>
                  ))
                ) : (
                  <option value="">{t("academyForm.coursePlaceholder")}</option>
                )}
              </select>
              <FiChevronDown aria-hidden />
            </div>

            <div className="academyContact-row academyContact-selectLike">
              <select
                value={form.yearsOfExperience}
                onChange={(e) => setForm((p) => ({ ...p, yearsOfExperience: e.target.value }))}
                aria-label={t("academyForm.experienceAriaLabel")}
              >
                <option value="">{t("academyForm.experiencePlaceholder")}</option>
                {EXPERIENCE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {t(`academyForm.experienceOptions.${option.key}`)}
                  </option>
                ))}
              </select>
              <FiChevronDown aria-hidden />
            </div>

            {error ? <p className="academyContact-message academyContact-messageError">{error}</p> : null}
            {success ? <p className="academyContact-message academyContact-messageSuccess">{success}</p> : null}

            <div className="academyContact-action">
              <button type="submit" className="academyContact-submit" disabled={submitting}>
                {submitting ? t("academyForm.submitting") : t("academyForm.submit")}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default GetInTouch;
